/**
 * Email typo correction (Task 4 of v/signup-may5).
 *
 * Catches the most common email-domain typos seen in our signup funnel
 * (Resend bounce log, last 6 days):
 *   icloud.con           → icloud.com
 *   gmail.con / .comd    → gmail.com
 *   gmal.com / gnail.com → gmail.com
 *   yahoo.con            → yahoo.com
 *   hotmail.con / hotmsil.com → hotmail.com
 *
 * Strategy:
 *   1. Direct map of known-misspelled domains → canonical.
 *   2. Levenshtein-distance ≤ 1 against a small dictionary of major
 *      providers, scoped to domains "close enough" to be confidently
 *      mistaken (length within 2 chars, same first letter).
 *
 * Returns { suggestion, reason } when a confident correction is found,
 * otherwise null. The UI renders this as a soft hint ("Did you mean
 * gmail.com?") — never blocks submission.
 *
 * Important: this is a SUGGESTION layer. We never reject the user's input.
 * False positives are bounded by the dictionary; uncommon real domains
 * (e.g. fastmail.com, pm.me, proton.me) will get no suggestion at all.
 */

// Canonical domains we suggest TO. Order matters only for stability of tests.
const CANONICAL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'live.com',
  'msn.com',
  'me.com',
  'protonmail.com',
] as const;

// Hard-coded mappings for the highest-frequency typos seen in the wild.
// These run BEFORE the Levenshtein pass and override it.
const DIRECT_TYPO_MAP: Record<string, string> = {
  // .con instead of .com (very common — adjacent key on most layouts)
  'gmail.con': 'gmail.com',
  'yahoo.con': 'yahoo.com',
  'hotmail.con': 'hotmail.com',
  'outlook.con': 'outlook.com',
  'icloud.con': 'icloud.com',
  'aol.con': 'aol.com',
  'live.con': 'live.com',
  'msn.con': 'msn.com',
  'me.con': 'me.com',

  // .comd / .cmo / .cm trailing typos
  'gmail.comd': 'gmail.com',
  'gmail.cmo': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'yahoo.comd': 'yahoo.com',
  'icloud.comd': 'icloud.com',

  // Common letter-swap / drop typos for Gmail (highest-volume domain)
  'gmal.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmaii.com': 'gmail.com',
  'gemail.com': 'gmail.com',
  'gmaim.com': 'gmail.com',
  'gmali.com': 'gmail.com',
  'gmail.co': 'gmail.com',

  // Common Yahoo
  'yhoo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',

  // Common Hotmail
  'hotmsil.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hormail.com': 'hotmail.com',
  'hotnail.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',

  // Common iCloud
  'iclod.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'icloud.co': 'icloud.com',

  // Common Outlook
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlook.co': 'outlook.com',
};

// Levenshtein distance between two strings. Iterative DP, O(n*m) time/space.
// Used only on short strings (email domains, ≤ ~30 chars), so cost is negligible.
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const m = a.length;
  const n = b.length;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,        // deletion
        curr[j - 1] + 1,    // insertion
        prev[j - 1] + cost  // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

export interface TypoSuggestion {
  /** The corrected full email (e.g. "user@gmail.com"). */
  suggestion: string;
  /** The corrected domain only (e.g. "gmail.com"). Useful for analytics. */
  suggestedDomain: string;
  /** What the user typed (e.g. "user@gmal.com"). */
  originalEmail: string;
  /** The domain the user typed (e.g. "gmal.com"). */
  originalDomain: string;
  /** Source of the suggestion: 'direct' = hardcoded map, 'fuzzy' = Levenshtein. */
  reason: 'direct' | 'fuzzy';
}

/**
 * Returns a suggested correction for the email's domain, or null if the
 * input looks fine (or we have no confident suggestion).
 */
export function suggestEmailCorrection(email: string): TypoSuggestion | null {
  if (!email || typeof email !== 'string') return null;

  const trimmed = email.trim().toLowerCase();
  const atIdx = trimmed.lastIndexOf('@');
  if (atIdx <= 0 || atIdx === trimmed.length - 1) {
    // No '@' or empty local-part / empty domain — no suggestion yet.
    return null;
  }

  const local = trimmed.slice(0, atIdx);
  const domain = trimmed.slice(atIdx + 1);

  // Don't suggest if the user already typed a canonical domain.
  if ((CANONICAL_DOMAINS as readonly string[]).includes(domain)) {
    return null;
  }

  // 1) Direct typo map.
  if (Object.prototype.hasOwnProperty.call(DIRECT_TYPO_MAP, domain)) {
    const suggestedDomain = DIRECT_TYPO_MAP[domain];
    return {
      suggestion: `${local}@${suggestedDomain}`,
      suggestedDomain,
      originalEmail: trimmed,
      originalDomain: domain,
      reason: 'direct',
    };
  }

  // 2) Fuzzy match against canonical providers.
  // Constraints to avoid false positives:
  //   * domain length is within 2 of canonical
  //   * first character matches (catches typos that aren't first-char swaps)
  //   * Levenshtein distance is exactly 1
  // Pick the canonical with the smallest distance ≤ 1 (ties → first hit).
  let best: { domain: string; dist: number } | null = null;
  for (const canonical of CANONICAL_DOMAINS) {
    if (Math.abs(canonical.length - domain.length) > 2) continue;
    if (canonical[0] !== domain[0]) continue;
    const d = levenshtein(domain, canonical);
    if (d === 1 && (best === null || d < best.dist)) {
      best = { domain: canonical, dist: d };
    }
  }

  if (best) {
    return {
      suggestion: `${local}@${best.domain}`,
      suggestedDomain: best.domain,
      originalEmail: trimmed,
      originalDomain: domain,
      reason: 'fuzzy',
    };
  }

  return null;
}
